import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { validateField, isValidPhone, sanitizePhone, isValidEmail, isValidName } from '../../utils/validationUtils';

const INQUIRY_TYPES = [
  'General Inquiry',
  'Course Information',
  'Enrollment & Subscription',
  'Technical Support',
  'Partnership & Collaboration',
  'Feedback & Suggestions',
  'Other',
];

const ContactModal = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', inquiryType: '', message: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    const val = name === 'phone' ? sanitizePhone(value) : value;
    setFormData(prev => ({ ...prev, [name]: val }));
    if (fieldErrors[name]) setFieldErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!value) return;
    const err = validateField(name, value);
    if (err) setFieldErrors(prev => ({ ...prev, [name]: err }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    setResult(null);

    const errors = {};
    if (!isValidName(formData.name)) errors.name = 'Name must be at least 2 characters';
    if (!isValidEmail(formData.email)) errors.email = 'Please enter a valid email address';
    if (formData.phone && !isValidPhone(formData.phone)) errors.phone = 'Please enter a valid phone number (7-15 digits)';
    if (!formData.message?.trim()) errors.message = 'Message is required';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSending(false);
      return;
    }

    try {
      const data = await api.sendContactMessage(formData);
      setResult(data.message);
      setFormData({ name: '', email: '', phone: '', inquiryType: '', message: '' });
      setFieldErrors({});
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError('');
    setFieldErrors({});
    onClose();
  };

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const inputCls = (field) =>
    `w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm ${fieldErrors[field] ? 'border-red-400' : 'border-dark-200'}`;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4" onClick={handleClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-dark-100 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-dark-900">Contact Us</h2>
            <p className="text-sm text-dark-500 mt-0.5">We'd love to hear from you</p>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-dark-100 rounded-lg transition-colors text-dark-400 hover:text-dark-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Success state */}
        {result ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-dark-900 mb-2">Message Sent!</h3>
            <p className="text-dark-500 text-sm leading-relaxed mb-6">{result}</p>
            <button onClick={handleClose} className="btn-primary px-8 py-2.5">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} onBlur={handleBlur} required minLength={2} placeholder="John Doe" className={inputCls('name')} />
                {fieldErrors.name && <p className="text-xs text-red-500 mt-1">{fieldErrors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleBlur} required placeholder="john@example.com" className={inputCls('email')} />
                {fieldErrors.email && <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Phone Number</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} onBlur={handleBlur} placeholder="+91 98765 43210" className={inputCls('phone')} />
                {fieldErrors.phone ? (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>
                ) : (
                  <p className="text-xs text-dark-400 mt-1">Include country code</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Inquiry Type</label>
                <select name="inquiryType" value={formData.inquiryType} onChange={handleChange} className="w-full px-3 py-2.5 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white">
                  <option value="">Select a topic</option>
                  {INQUIRY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-700 mb-1">Message <span className="text-red-500">*</span></label>
              <textarea name="message" value={formData.message} onChange={handleChange} onBlur={handleBlur} required rows={4} maxLength={2000} placeholder="Tell us more about your query..." className={`${inputCls('message')} resize-none`} />
              <div className="flex justify-between mt-1">
                {fieldErrors.message ? <p className="text-xs text-red-500">{fieldErrors.message}</p> : <span />}
                <p className="text-xs text-dark-400">{formData.message.length}/2000</p>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={sending} className="flex-1 btn-primary py-2.5 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                {sending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Message
                  </>
                )}
              </button>
              <button type="button" onClick={handleClose} className="px-6 py-2.5 border border-dark-200 rounded-lg text-sm font-medium text-dark-700 hover:bg-dark-50">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ContactModal;
